import React from "react";
import { Box, Typography, Container, Grid } from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import { PlayCircle, Clock, CheckCircle, AlertTriangle, TrendingUp } from "lucide-react";
import { motion } from "framer-motion";
import PhoneMockup from "../components/PhoneMockup";

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
`;

const recommendations = [
  "High-complexity tests",
  "Chronic case monitoring",
  "Doctor explanations",
];

const SectionContainer = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(12),
  paddingBottom: theme.spacing(12),
  [theme.breakpoints.down("md")]: {
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
  },
}));

const GradientPrimary = styled(Box)(({ theme }) => ({
  background: "linear-gradient(135deg, #3567D6 0%, #1D47A4 100%)",
  borderRadius: "24px",
  padding: theme.spacing(8),
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(6),
  },
}));

const MockupContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  [theme.breakpoints.down("lg")]: {
    marginBottom: theme.spacing(6),
  },
}));

const ContentBox = styled(Box)(({ theme }) => ({
  color: "#FFFFFF",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(4),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "2.5rem",
  fontWeight: 700,
  lineHeight: 1.2,
  color: "#FFFFFF",
  letterSpacing: "-0.01em",
  [theme.breakpoints.down("md")]: {
    fontSize: "2rem",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.75rem",
  },
}));

const Description = styled(Typography)(({ theme }) => ({
  color: "rgba(255, 255, 255, 0.9)",
  lineHeight: 1.75,
  fontSize: "1.125rem",
  [theme.breakpoints.down("sm")]: {
    fontSize: "1rem",
  },
}));

const RecommendationsSection = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const RecommendationsHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
}));

const HeaderDot = styled(Box)({
  width: "8px",
  height: "8px",
  borderRadius: "50%",
  backgroundColor: "#FFFFFF",
});

const HeaderText = styled(Typography)({
  fontWeight: 600,
  color: "#FFFFFF",
  fontSize: "1.125rem",
});

const RecommendationsList = styled("ul")(({ theme }) => ({
  listStyle: "none",
  padding: 0,
  margin: 0,
  marginLeft: theme.spacing(4),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5),
}));

const RecommendationItem = styled("li")({
  color: "rgba(255, 255, 255, 0.9)",
  fontSize: "1rem",
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
});

const VideoScreenContent = styled(Box)(({ theme }) => ({
  height: "100%",
  minHeight: "100%",
  background: "linear-gradient(135deg, #1D47A4 0%, #3567D6 100%)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1.5),
  padding: theme.spacing(2),
  position: "relative",
  overflow: "hidden",
  [theme.breakpoints.down("md")]: {
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
  },
  [theme.breakpoints.down("sm")]: {
    gap: theme.spacing(0.75),
    padding: theme.spacing(1),
  },
}));

const PlayButton = styled(motion.div)(({ theme }) => ({
  width: "80px",
  height: "80px",
  borderRadius: "50%",
  backgroundColor: "rgba(255, 255, 255, 0.2)",
  backdropFilter: "blur(10px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  animation: `${pulse} 2s ease-in-out infinite`,
  flexShrink: 0,
  [theme.breakpoints.down("md")]: {
    width: "64px",
    height: "64px",
  },
  [theme.breakpoints.down("sm")]: {
    width: "48px",
    height: "48px",
  },
}));

const VideoTitle = styled(Typography)(({ theme }) => ({
  color: "#FFFFFF",
  fontSize: "1rem",
  fontWeight: 600,
  textAlign: "center",
  lineHeight: 1.3,
  padding: `0 ${theme.spacing(1)}`,
  [theme.breakpoints.down("md")]: {
    fontSize: "0.875rem",
    padding: `0 ${theme.spacing(0.75)}`,
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.75rem",
    padding: `0 ${theme.spacing(0.5)}`,
  },
}));

const VideoStats = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(1),
  flexWrap: "wrap",
  justifyContent: "center",
  width: "100%",
  padding: `0 ${theme.spacing(1)}`,
  [theme.breakpoints.down("md")]: {
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
    padding: `0 ${theme.spacing(0.75)}`,
  },
  [theme.breakpoints.down("sm")]: {
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.25),
    padding: `0 ${theme.spacing(0.5)}`,
  },
}));

const StatItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  backgroundColor: "rgba(255, 255, 255, 0.15)",
  padding: theme.spacing(0.75, 1),
  borderRadius: "6px",
  flexShrink: 0,
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(0.5, 0.75),
    borderRadius: "4px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.375, 0.5),
    borderRadius: "4px",
  },
}));

const StatText = styled(Typography)(({ theme }) => ({
  color: "#FFFFFF",
  fontSize: "0.75rem",
  fontWeight: 600,
  whiteSpace: "nowrap",
  [theme.breakpoints.down("md")]: {
    fontSize: "0.625rem",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.5rem",
  },
}));

const ProgressBar = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: theme.spacing(2),
  left: theme.spacing(2),
  right: theme.spacing(2),
  height: "3px",
  backgroundColor: "rgba(255, 255, 255, 0.2)",
  borderRadius: "2px",
  overflow: "hidden",
  [theme.breakpoints.down("md")]: {
    bottom: theme.spacing(1.5),
    left: theme.spacing(1.5),
    right: theme.spacing(1.5),
    height: "2px",
  },
  [theme.breakpoints.down("sm")]: {
    bottom: theme.spacing(1),
    left: theme.spacing(1),
    right: theme.spacing(1),
    height: "2px",
  },
}));

const ProgressFill = styled(motion.div)({
  height: "100%",
  backgroundColor: "#FFFFFF",
  borderRadius: "2px",
});

const TestResultsList = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.75),
  width: "100%",
  padding: `0 ${theme.spacing(1)}`,
  marginTop: theme.spacing(0.5),
  [theme.breakpoints.down("md")]: {
    gap: theme.spacing(0.5),
    padding: `0 ${theme.spacing(0.75)}`,
  },
  [theme.breakpoints.down("sm")]: {
    gap: theme.spacing(0.375),
    padding: `0 ${theme.spacing(0.5)}`,
  },
}));

const TestResultItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
  backgroundColor: "rgba(255, 255, 255, 0.1)",
  padding: theme.spacing(0.5, 0.75),
  borderRadius: "6px",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(0.375, 0.5),
    borderRadius: "4px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.25, 0.5),
    borderRadius: "4px",
  },
}));

const TestResultText = styled(Typography)(({ theme }) => ({
  color: "#FFFFFF",
  fontSize: "0.625rem",
  fontWeight: 500,
  flex: 1,
  [theme.breakpoints.down("md")]: {
    fontSize: "0.5rem",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.4rem",
  },
}));

const VideoReportSection = () => {
  return (
    <SectionContainer>
      <Container>
        <GradientPrimary>
          <Grid container spacing={{ xs: 4, sm: 5, md: 6 }} alignItems="center">
            {/* Left - Phone Mockup */}
            <Grid item xs={12} lg={6}>
              <MockupContainer>
                <PhoneMockup>
                  <VideoScreenContent>
                    <VideoTitle>Video Report</VideoTitle>
                    <VideoTitle sx={{ fontSize: { xs: "0.65rem", md: "0.75rem", lg: "0.875rem" }, fontWeight: 400, opacity: 0.9 }}>
                      Lab Test Results Summary
                    </VideoTitle>

                    <PlayButton
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Box
                        component={PlayCircle}
                        sx={{
                          width: { xs: "32px", md: "36px", lg: "40px" },
                          height: { xs: "32px", md: "36px", lg: "40px" },
                          color: "#FFFFFF",
                        }}
                      />
                    </PlayButton>

                    <TestResultsList>
                      <TestResultItem>
                        <Box
                          component={AlertTriangle}
                          sx={{
                            width: { xs: "10px", md: "11px", lg: "12px" },
                            height: { xs: "10px", md: "11px", lg: "12px" },
                            color: "#FFD700",
                            flexShrink: 0,
                          }}
                        />
                        <TestResultText>High Cholesterol Detected</TestResultText>
                      </TestResultItem>
                      <TestResultItem>
                        <Box
                          component={TrendingUp}
                          sx={{
                            width: { xs: "10px", md: "11px", lg: "12px" },
                            height: { xs: "10px", md: "11px", lg: "12px" },
                            color: "#4CAF50",
                            flexShrink: 0,
                          }}
                        />
                        <TestResultText>Blood Sugar Normal</TestResultText>
                      </TestResultItem>
                    </TestResultsList>

                    <VideoStats>
                      <StatItem>
                        <Box
                          component={Clock}
                          sx={{
                            width: { xs: "10px", md: "11px", lg: "12px" },
                            height: { xs: "10px", md: "11px", lg: "12px" },
                            color: "#FFFFFF",
                            flexShrink: 0,
                          }}
                        />
                        <StatText>60 sec</StatText>
                      </StatItem>
                      <StatItem>
                        <Box
                          component={CheckCircle}
                          sx={{
                            width: { xs: "10px", md: "11px", lg: "12px" },
                            height: { xs: "10px", md: "11px", lg: "12px" },
                            color: "#FFFFFF",
                            flexShrink: 0,
                          }}
                        />
                        <StatText>Auto Generated</StatText>
                      </StatItem>
                    </VideoStats>

                    <ProgressBar>
                      <ProgressFill
                        initial={{ width: "0%" }}
                        animate={{ width: "65%" }}
                        transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                      />
                    </ProgressBar>
                  </VideoScreenContent>
                </PhoneMockup>
              </MockupContainer>
            </Grid>

            {/* Right - Content */}
            <Grid item xs={12} lg={6}>
              <ContentBox>
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <SectionTitle>
                    A human-friendly
                    <br />
                    interactive video format
                  </SectionTitle>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <Description>
                    Simplifies complex medical data in under a minute—making health reports understandable for every patient.
                  </Description>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <RecommendationsSection>
                    <RecommendationsHeader>
                      <HeaderDot />
                      <HeaderText>Recommended for:</HeaderText>
                    </RecommendationsHeader>
                    <RecommendationsList>
                      {recommendations.map((item) => (
                        <RecommendationItem key={item}>
                          <CheckCircle style={{ width: "16px", height: "16px" }} />
                          {item}
                        </RecommendationItem>
                      ))}
                    </RecommendationsList>
                  </RecommendationsSection>
                </motion.div>
              </ContentBox>
            </Grid>
          </Grid>
        </GradientPrimary>
      </Container>
    </SectionContainer>
  );
};

export default VideoReportSection;